import { RepeatIcon } from "@chakra-ui/icons";
import { Alert, AlertIcon, Box, Heading, HStack, IconButton, Input, Spinner, VStack } from "@chakra-ui/react";
import type { Order } from "@shared/models";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import OrderCard from "~/components/OrderCard";
import PaginationWrapper from "~/components/PaginationWrapper";
import Meta from "~/layout/Meta";
import { AppConfig } from "~/services/app-config-service";
import { fetchArray } from "~/services/fetch-array";

const HomePage = () => {
  const [searchText, setSearchText] = useState("");
  const [filteredResults, setFilteredResults] = useState<Order[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 50;

  const recentOrders = useQuery({
    queryKey: ["completed-orders"],
    queryFn: () => fetchArray<Order>("/api/orders/all", "completed orders")
  });

  useEffect(() => {
    if (!recentOrders.data) return;
    setFilteredResults(recentOrders.data);
  }, [recentOrders.data]);

  useEffect(() => {
    if (!searchText) {
      setFilteredResults(recentOrders.data || []);
    } else {
      const filteredResults = recentOrders.data?.filter((x) => {
        return JSON.stringify(x).toLocaleLowerCase().includes(searchText.toLowerCase());
      });
      setFilteredResults(filteredResults || []);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchText, recentOrders.data]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredResults.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const endIndex = startIndex + ordersPerPage;
  const currentOrders = filteredResults.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (recentOrders.isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" pt={10}>
        <Spinner />
      </Box>
    );
  }

  return (
    <>
      <Meta title="Completed" description={AppConfig.brandConfig.meta.defaultDescription} />
      <HStack mb="4">
        <Heading as="h2">Completed Orders</Heading>
        <IconButton aria-label="Refresh Orders" icon={<RepeatIcon />} onClick={() => recentOrders.refetch()} />
      </HStack>
      <Input
        value={searchText}
        placeholder="Search orders..."
        mb={4}
        onChange={(e) => {
          setSearchText(e.target.value);
        }}
      />
      {recentOrders.isError && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {recentOrders.error.message}
        </Alert>
      )}
      {!recentOrders.isError && filteredResults.length === 0 && <Box pl={4}>No results</Box>}

      <VStack w="full" alignItems="stretch" overflow={"hidden"}>
        {currentOrders?.map((order) => (
          <OrderCard key={order.id} order={order} />
        ))}
      </VStack>

      <PaginationWrapper
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        startIndex={startIndex}
        endIndex={endIndex}
        totalItems={filteredResults.length}
        itemType="orders"
      />
    </>
  );
};

export default HomePage;
